import { FC, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { TbChalkboard, TbArrowsMaximize, TbSearch } from 'react-icons/tb';
import { Tutorial, TUTORIALS } from 'src/components/TutorialModal';

const TutorialCard: FC<{
  tutorial: Tutorial;
  onExpand: (tutorial: Tutorial) => void;
}> = ({ tutorial, onExpand }) => {
  return (
    <div
      className="group relative cursor-pointer overflow-hidden rounded-[0.75rem] shadow-sm duration-300"
      style={{
        background: 'linear-gradient(135deg, #eef2ff, #f1efff)',
        border: '1px solid rgba(59, 130, 246, 0.20)'
      }}
      onClick={() => onExpand(tutorial)}
    >
      <div className="relative aspect-video w-full overflow-hidden">
        <img
          src={tutorial.gif}
          alt={tutorial.title}
          className="h-full w-full object-cover"
        />

        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <div className="flex flex-col items-center gap-2 text-white">
            <TbArrowsMaximize className="text-4xl drop-shadow" />
            <span className="text-sm font-semibold drop-shadow">Expand</span>
          </div>
        </div>
      </div>

      <div className="p-[1.25rem]">
        <div className="mb-1" style={{ fontFamily: 'Roboto', fontWeight: 600, fontSize: '16px', color: '#000000' }}>{tutorial.title}</div>
        <div style={{ fontFamily: 'Roboto', fontWeight: 400, fontSize: '12px', color: '#1D1D1D' }}>{tutorial.description}</div>
      </div>
    </div>
  );
};

const Tutorials: FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredTutorials = useMemo(() => {
    if (!searchTerm.trim()) return TUTORIALS;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return TUTORIALS.filter(tutorial =>
      tutorial.title.toLowerCase().includes(lowerSearchTerm) ||
      tutorial.description.toLowerCase().includes(lowerSearchTerm)
    );
  }, [searchTerm]);

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center font-bold text-blue-600">
          <TbChalkboard className="mr-2 inline-block text-2xl" />
          <span className="align-middle">How To's</span>
        </div>

        <div className="relative w-72">
          <TbSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search tutorials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-10 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      {filteredTutorials.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-gray-500">
          <span>No tutorials found matching "{searchTerm}"</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {filteredTutorials.map((tutorial) => (
            <TutorialCard
              key={tutorial.id}
              tutorial={tutorial}
              onExpand={(t) => navigate(`${pathname}?feature=${t.id}`, { preventScrollReset: true })}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default Tutorials;
